import {
  FC,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useAppDispatch } from '../../../hooks';
import { updateMeal, updateVariant } from '../../../store/slices';
import { ICreateMeal, ICreateVariant } from '../../../types';
import { mealsApi } from '../../../api';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { LogoutLoader } from './loaders';

interface IToggle {
  active: boolean;
  module?: string;
  primaryKey: string | number;
  item?: any;
  onToggle?: (value: boolean) => void;
}

export const Toggle: FC<IToggle> = ({
  active,
  module,
  primaryKey,
  onToggle,
}) => {
  const queryClient = useQueryClient();

  const dispatch = useAppDispatch();
  const [internalOn, setInternalOn] = useState(active);
  const [loading, setLoading] = useState(false);

  // When the parent provides onToggle, treat the component as fully controlled
  // so the visual state always tracks the parent's `active` prop and can't
  // drift from it. Legacy `module`-based usages still rely on internal state.
  const on = onToggle ? active : internalOn;

  const handleToggle = async (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (onToggle) {
      onToggle(!active);
      return;
    }

    if (module === 'meals') {
      dispatch(
        updateMeal({
          payload: {
            status: active ? 'OUT_OF_STOCK' : 'ACTIVE',
          } as ICreateMeal,
          id: primaryKey as number,
        })
      );
    }

    if (module === 'variants') {
      dispatch(
        updateVariant({
          payload: {
            status: active ? 'OUT_OF_STOCK' : 'ACTIVE',
          } as ICreateVariant,
          id: primaryKey as number,
        })
      );
    }

    if (module === 'options') {
      setLoading(true);
      try {
        await mealsApi.updateOption(primaryKey.toString(), { active: !on });
        if (queryClient) {
          await queryClient.refetchQueries(['options']);
        }

        toast.success('Option updated!');
        setInternalOn((prev) => !prev);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }

    if (module === 'foodItems') {
      setLoading(true);
      try {
        await mealsApi.updateFoodItem(primaryKey.toString(), {
          status: on ? 'OUT_OF_STOCK' : 'OPEN',
        });
        if (queryClient) {
          await queryClient.refetchQueries(['food-items']);
        }
        toast.success('Food item updated!');
        setInternalOn((prev) => !prev);
      } catch (error) {
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    setInternalOn(active);
  }, [active]);

  return (
    <button
      className={`h-8 w-16 px-1 flex rounded-full items-center transition duration-300 relative ${
        on ? 'bg-primary-700' : 'bg-neutral-400'
      }`}
      onClick={handleToggle}
      disabled={loading}
      aria-busy={loading}
      aria-disabled={loading}
    >
      {loading ? (
        <div className="flex justify-center items-center w-full">
          <LogoutLoader heightWidth="w-5 h-5" />
        </div>
      ) : (
        <>
          {!on && (
            <p className="absolute top-1.5 right-2 text-sm semibold">NO</p>
          )}
          <div
            className={`h-5 w-5 rounded-full bg-white transition duration-300 ${
              on ? 'translate-x-9' : 'translate-x-0'
            }`}
          />
          {on && (
            <p className="absolute top-1.5 left-2 text-sm semibold text-white">
              YES
            </p>
          )}
        </>
      )}
    </button>
  );
};

export const CheckboxToggle: FC<IToggle> = ({ active, primaryKey, module }) => {
  const dispatch = useAppDispatch();
  const [on, setOn] = useState(active);

  const handleToggle = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (module === 'variants') {
      dispatch(
        updateVariant({
          payload: {
            status: active ? 'INACTIVE' : 'ACTIVE',
          } as ICreateVariant,
          id: primaryKey as number,
        })
      );
    } else {
      dispatch(
        updateMeal({
          payload: {
            status: active ? 'INACTIVE' : 'ACTIVE',
          } as ICreateMeal,
          id: primaryKey as number,
        })
      );
    }

    setOn((prev) => !prev);
  };

  useEffect(() => {
    setOn(active);
  }, [active]);

  return (
    <div>
      <input
        type="checkbox"
        onChange={handleToggle}
        checked={!on}
        className="h-5 w-5"
      />
    </div>
  );
};

export const ToggleGroup = ({
  items,
  activeKey,
  onClick,
}: {
  items: string[];
  activeKey: string;
  onClick: (x: string) => void;
}) => {
  const [sliderStyle, setSliderStyle] = useState({
    width: 0,
    left: 0,
  });
  const buttonsRef = useRef<Map<string, HTMLButtonElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  const updateSliderPosition = useCallback(() => {
    const button = buttonsRef.current.get(activeKey);
    if (button && containerRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const buttonRect = button.getBoundingClientRect();
      setSliderStyle({
        width: buttonRect.width,
        left: buttonRect.left - containerRect.left,
      });
    }
  }, [activeKey]);

  useEffect(() => {
    updateSliderPosition();
    window.addEventListener('resize', updateSliderPosition);
    return () => window.removeEventListener('resize', updateSliderPosition);
  }, [updateSliderPosition]);

  return (
    <div
      ref={containerRef}
      className="rounded-lg bg-gray-200 px-1 py-1 inline-flex gap-4 mt-4 relative"
    >
      <div
        className="absolute bg-white rounded-lg transition-all duration-300 ease-in-out"
        style={{
          top: '6px',
          width: sliderStyle.width,
          left: sliderStyle.left,
          height: 'calc(100% - 12px)',
          boxShadow: '0px 1px 4px 0px #0000001F',
        }}
      />

      {items.map((item) => (
        <button
          key={item}
          ref={(el) => {
            if (el) buttonsRef.current.set(item, el);
          }}
          className={`cursor-pointer rounded-lg relative z-10 px-3 py-1.5 text-sm transition-colors duration-300 w-26 lg:w-auto text-neutral-body`}
          onClick={() => onClick(item)}
        >
          {item}
        </button>
      ))}
    </div>
  );
};
